import { useMemo } from 'react';
import Link from 'next/link';
import { Cell, Funnel, FunnelChart, ResponsiveContainer } from 'recharts';

import { Box, Button, ButtonGroup, Card, Divider, Heading, Modal, Paragraph, Text } from '@zillow/constellation-10';

import { ROUTES } from '../../constants/urls';

type ListingPerformanceModalProps = {
  onClose: () => void;
  prospectsThisMonth: number;
  viewsThisMonth: number;
  listingAlias: string;
};

const ListingPerformanceModal = ({
  onClose,
  prospectsThisMonth,
  viewsThisMonth,
  listingAlias,
}: ListingPerformanceModalProps) => {
  const impressionsThisMonth = 2396;
  const minimumFunnelStageValue = impressionsThisMonth * 0.04;
  const getVisibleFunnelValue = (value: number) => Math.max(value, minimumFunnelStageValue);

  const performanceFunnelData = useMemo(
    () => [
      {
        name: 'Impressions',
        displayValue: impressionsThisMonth.toLocaleString(),
        visualValue: getVisibleFunnelValue(impressionsThisMonth),
        color: '#9acdff',
      },
      {
        name: 'Views',
        displayValue: String(viewsThisMonth),
        visualValue: getVisibleFunnelValue(viewsThisMonth),
        color: '#e6a8ff',
      },
      {
        name: 'Prospects',
        displayValue: String(prospectsThisMonth),
        visualValue: getVisibleFunnelValue(prospectsThisMonth),
        color: '#9ff17b',
      },
    ],
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [prospectsThisMonth, viewsThisMonth],
  );

  const funnelRates = useMemo(
    () => [
      {
        label: 'Page views per impression',
        value: `${((viewsThisMonth / impressionsThisMonth) * 100).toFixed(1)}%`,
      },
      {
        label: 'Prospects per page view',
        value: viewsThisMonth > 0 ? `${((prospectsThisMonth / viewsThisMonth) * 100).toFixed(1)}%` : '-',
      },
    ],
    [prospectsThisMonth, viewsThisMonth],
  );

  return (
    <Modal.Root open={true} onOpenChange={(o) => !o && onClose()} size="md">
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                Listing performance
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
                <Paragraph>
                  See how renters moved from finding your listing in search to viewing it and becoming prospects so far
                  this month to date.
                </Paragraph>
                <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
                  <Box
                    css={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 2fr) minmax(10rem, 1fr)',
                      width: '100%',
                      maxWidth: '48rem',
                      height: '320px',
                      marginInline: 'auto',
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <FunnelChart>
                        <Funnel data={performanceFunnelData} dataKey="visualValue" isAnimationActive={false}>
                          {performanceFunnelData.map((stage) => (
                            <Cell key={stage.name} fill={stage.color} />
                          ))}
                        </Funnel>
                      </FunnelChart>
                    </ResponsiveContainer>
                    <Box css={{ display: 'grid', gridTemplateRows: 'repeat(3, minmax(0, 1fr))' }}>
                      {performanceFunnelData.map((stage) => (
                        <Box
                          key={stage.name}
                          css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                        >
                          <Text textStyle="body-sm">{stage.name}</Text>
                          <Text textStyle="body-sm">{stage.displayValue}</Text>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    css={{
                      display: 'grid',
                      gridTemplateColumns: { base: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: '300',
                    }}
                  >
                    {funnelRates.map((rate) => (
                      <Card key={rate.label} elevated={false}>
                        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', height: '100%' }}>
                          <Text>{rate.label}</Text>
                          <Heading level={4}>{rate.value}</Heading>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Listing performance actions">
                <Modal.Close>
                  <Button tone="brand" emphasis="bare">
                    Close
                  </Button>
                </Modal.Close>
                <Button tone="brand" emphasis="filled" asChild>
                  <Link href={{ pathname: ROUTES.PREMIUM_ANALYTICS, query: { listingAlias } }}>
                    View listing performance
                  </Link>
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default ListingPerformanceModal;
